import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { MeasureField, UnitToggle, type Units } from "./MeasureField";
import { NoticeBox } from "@/components/NoticeBox";

function calcFormulas(gender: "male" | "female", heightCm: number) {
  const totalInches = heightCm / 2.54;
  const inchesOver5ft = Math.max(0, totalInches - 60);

  // Devine (1974)
  const devine = gender === "male" ? 50 + 2.3 * inchesOver5ft : 45.5 + 2.3 * inchesOver5ft;
  // Robinson (1983)
  const robinson = gender === "male" ? 52 + 1.9 * inchesOver5ft : 49 + 1.7 * inchesOver5ft;
  // Miller (1983)
  const miller = gender === "male" ? 56.2 + 1.41 * inchesOver5ft : 53.1 + 1.36 * inchesOver5ft;
  // Hamwi (1964)
  const hamwi = gender === "male" ? 48 + 2.7 * inchesOver5ft : 45.5 + 2.2 * inchesOver5ft;

  return { devine, robinson, miller, hamwi };
}

const TOOL_INFO_CONTENT = {
  what: `Estimates an ideal body weight range based on height, using several established formulas, as one general reference point among many possible ways to think about a healthy weight.`,
  how: `Enter your height and sex, and estimated ideal weight ranges from a few different standard formulas are shown for comparison.`,
  tip: `These formulas don't account for frame size, muscle mass, or individual body composition — treat the range as a general reference, not a precise personal target.`,
};

export default function IdealWeightCalculator() {
  const [units, setUnits] = useState<Units>("metric");
  const [gender, setGender] = useState<"male" | "female">("male");
  const [height, setHeight] = useState<number | null>(175);

  const valid = height != null;
  const { devine, robinson, miller, hamwi } = valid ? calcFormulas(gender, height!) : { devine: 0, robinson: 0, miller: 0, hamwi: 0 };
  const average = (devine + robinson + miller + hamwi) / 4;
  const hm = valid ? height! / 100 : 0;
  const w = (kg: number) => (units === "metric" ? `${kg.toFixed(1)} kg` : `${(kg / 0.45359237).toFixed(0)} lb`);

  const tooShort = valid && height! < 152.4;
  const { blocked } = useLogToolUsage(valid);

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <UnitToggle units={units} onChange={setUnits} />
        <Tabs value={gender} onValueChange={v => setGender(v as "male" | "female")}>
          <TabsList className="w-full">
            <TabsTrigger value="male" className="flex-1">Male</TabsTrigger>
            <TabsTrigger value="female" className="flex-1">Female</TabsTrigger>
          </TabsList>
        </Tabs>

        <MeasureField label="Height" kind="height" units={units} initialMetric={175} onValue={setHeight} />

        {valid && !blocked && (
          <>
            <div className="p-4 rounded-xl border bg-primary/5 text-center">
              <p className="text-xs text-muted-foreground">Average Ideal Weight</p>
              <p className="text-2xl font-bold text-primary" data-testid="iw-average">{w(average)}</p>
              <p className="text-xs text-muted-foreground mt-1" data-testid="iw-range">Healthy BMI range (18.5–24.9) for your height: {w(18.5 * hm * hm)} to {w(24.9 * hm * hm)}</p>
            </div>
            {tooShort && <NoticeBox className="text-xs" role="alert" testId="iw-short-warning">These formulas start from a 5 ft (152 cm) base and were not built for shorter heights, so the figures below are held at that base and can sit above the healthy BMI range. Use the BMI range above instead.</NoticeBox>}
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Devine Formula</p><p className="text-sm font-bold">{w(devine)}</p></div>
              <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Robinson Formula</p><p className="text-sm font-bold">{w(robinson)}</p></div>
              <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Miller Formula</p><p className="text-sm font-bold">{w(miller)}</p></div>
              <div className="text-center p-3 rounded-lg border bg-card"><p className="text-xs text-muted-foreground">Hamwi Formula</p><p className="text-sm font-bold">{w(hamwi)}</p></div>
            </div>
          </>
        )}

        <p className="text-xs text-muted-foreground text-center">
          These are population-average formulas from the 1960s-80s: they don't account for muscle mass, frame size, or individual variation. Not medical advice.
        </p>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
